import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

interface TextInputProps {
  defaultValue?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  suffix?: string;
}

export const TextInput: React.FC<TextInputProps> = ({
  defaultValue = '',
  onValueChange,
  placeholder = 'Voer waarde in...',
  minLength = 1,
  maxLength = 500,
  required = true,
  suffix,
}) => {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue(defaultValue);
    setError(null);
  }, [defaultValue]);

  const validateInput = (input: string): boolean => {
    // Check if required but empty
    if (required && input.trim().length === 0) {
      setError('Dit veld is verplicht');
      toast.error('Dit veld mag niet leeg zijn');
      return false;
    }

    // Check minimum length
    if (input.length > 0 && input.length < minLength) {
      setError(`Minimaal ${minLength} karakter${minLength > 1 ? 's' : ''} vereist`);
      toast.error(`Voer minimaal ${minLength} karakter${minLength > 1 ? 's' : ''} in`);
      return false;
    }

    // Check maximum length
    if (input.length > maxLength) {
      setError(`Maximaal ${maxLength} karakters toegestaan`);
      toast.error(`Maximaal ${maxLength} karakters toegestaan`);
      return false;
    }

    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setError(null);

    // Validate and update parent
    if (validateInput(newValue)) {
      onValueChange(newValue);
    } else {
      onValueChange(''); // Send empty string on validation failure
    }
  };

  const handleBlur = () => {
    // Re-validate on blur to show final errors
    if (value.trim().length > 0) {
      validateInput(value);
    }
  };

  return (
    <div className="p-4" onClick={(e) => e.stopPropagation()}>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={(e) => e.stopPropagation()}
          placeholder={placeholder}
          minLength={minLength}
          maxLength={maxLength}
          required={required}
          title={`${required ? 'Verplicht veld. ' : ''}Voer minimaal ${minLength} en maximaal ${maxLength} karakters in`}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors ${
            suffix ? 'pr-12' : ''
          } ${
            error
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-neutral-primary/20 focus:ring-moss-primary focus:border-transparent'
          }`}
          autoFocus
        />
        {suffix && (
          <span className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm">
            {suffix}
          </span>
        )}
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}
      {!error && maxLength && (
        <p className="text-xs text-neutral-secondaryMid mt-1">
          {value.length} / {maxLength} karakters
        </p>
      )}
    </div>
  );
};
